import { KaraokeTrackManager } from '@src/editor/lib/KaraokeTrackManager';
import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';

import { AbstractCommand } from '../AbstractCommand';
import { TimelineMode } from '../TimelineMode';

import type { TranscriptTrackManager } from '@src/editor/lib/TranscriptTrackManager';
import type { HasClipTranscriptSegment } from '@src/editor/models/elements/HasClipTranscriptSegment';
import type { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import type {
  ClipTranscriptTextToken,
  ElementResizeResult,
} from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

type OperationType =
  | 'delete'
  | 'trimStart'
  | 'trimEnd'
  | 'excise'
  | 'removePunctuation'
  | 'none';

type OperationResult = {
  operation: OperationType;
  removedSegment: ClipTranscriptSegment | null;
};

/**
 * TranscriptSimpleRemoveCommand – removes a contiguous range of transcript
 * words from a VideoTranscriptElement (specified by their original `initial_index` values).
 * It determines the minimal operation required:
 *   • delete – when removing all words from the element
 *   • trimStart – when removing words from the beginning (respects trimHead flag)
 *   • trimEnd – when removing words from the end (respects trimTail flag)
 *   • excise – when removing words from the middle, cutting out a section
 *
 * The trimHead and trimTail flags control whether parts of the video element
 * outside the removed words range should be kept (intro/outro) or trimmed away.
 *
 * The command focuses on transcript track operations and creates removed
 * clip segments to maintain transcript completeness for undo operations.
 */
export class TranscriptRemoveCommand extends AbstractCommand {
  getName(): string {
    return 'TranscriptRemoveCommand';
  }

  private readonly segmentId: string;
  private readonly fromIndex: number;
  private readonly toIndex: number;
  private readonly trimHead: boolean;
  private readonly trimTail: boolean;

  private transcriptTrack: TranscriptTrack | null = null;
  private readonly transcriptTrackManager: TranscriptTrackManager;

  private transcriptTrackElements: readonly HasClipTranscriptSegment[] = [];

  private timelineSegmentRemoved: ElementResizeResult | null = null;

  private operationResult: OperationResult | null = null;

  constructor(
    editor: VideoEditor,
    segmentId: string,
    fromIndex: number,
    toIndex: number,
    trimHead: boolean,
    trimTail: boolean,
  ) {
    super(editor);
    this.segmentId = segmentId;
    this.fromIndex = Math.min(fromIndex, toIndex);
    this.toIndex = Math.max(fromIndex, toIndex);
    this.trimHead = trimHead;
    this.trimTail = trimTail;

    this.transcriptTrackManager = this.editor.getTranscriptTrackManager();

    this.transcriptTrack = editor.getVideoTranscriptTrack()!;
    this.transcriptTrackElements =
      this.transcriptTrack.transcriptSegmentElements;
  }

  // -------------------------------------------------------------------
  // Command lifecycle
  // -------------------------------------------------------------------

  execute(): boolean {
    const track = this.transcriptTrack;
    if (!track) {
      return false;
    }

    const element = this.lookupElement();
    if (!element) {
      return false;
    }

    const op = this.determineOperation(element);
    if (op === 'none') {
      return false;
    }

    this.applyOperation(element, op);

    // Recalculate timeline and mark dirty
    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_remove');

    // Reconcile karaoke track after remove operation
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (karaokeTrack) {
      const karaokeTrackManager = new KaraokeTrackManager(this.editor);
      karaokeTrackManager.reconcileKaraokeTrack();
    }

    return true;
  }

  undo(): boolean {
    throw new Error('Method not implemented.');
  }

  getDescription(): string {
    return `Simple remove words (${this.fromIndex}-${this.toIndex}) from segment ${this.segmentId}`;
  }

  getOperationResult(): OperationResult | null {
    return this.operationResult;
  }

  getTimelineSegmentRemoved(): ElementResizeResult | null {
    return this.timelineSegmentRemoved;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private getTranscriptTrack(): TranscriptTrack | null {
    if (this.transcriptTrack) return this.transcriptTrack;
    const track = this.editor.getVideoTranscriptTrack();
    if (!track) return null;
    this.transcriptTrack = track;
    return track;
  }

  private lookupElement(): VideoTranscriptElement | null {
    const element = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.segmentId,
    );
    if (!(element instanceof VideoTranscriptElement)) {
      return null;
    }
    return element;
  }

  private determineOperation(element: VideoTranscriptElement): OperationType {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return 'none';

    const words = segment.getWords();
    const wordsWithPunctuation = segment.getWordsWithPunctuation();

    // Check if range contains only punctuation first (before checking words.length)
    const tokensInRange = wordsWithPunctuation.filter(
      (_, idx) => idx >= this.fromIndex && idx <= this.toIndex,
    );
    const isPunctuationOnly = tokensInRange.every(
      token => token.type === 'punct',
    );

    if (isPunctuationOnly && tokensInRange.length > 0) {
      return 'removePunctuation';
    }

    // Now check for word operations
    if (words.length === 0) return 'none';

    const wordsToRemove = this.getWordsToRemove(element);
    if (wordsToRemove.length === 0) return 'none';

    const rangeHitsStart =
      this.fromIndex <= wordsWithPunctuation.indexOf(segment.firstWord!) &&
      this.trimHead;
    const rangeHitsEnd =
      this.toIndex >= wordsWithPunctuation.indexOf(segment.lastWord!) &&
      this.trimTail;

    if (rangeHitsStart && rangeHitsEnd) {
      return 'delete';
    }
    if (rangeHitsStart) {
      return 'trimStart';
    }
    if (rangeHitsEnd) {
      return 'trimEnd';
    }

    // Middle section removal (excise)
    return 'excise';
  }

  private applyOperation(
    element: VideoTranscriptElement,
    op: OperationType,
  ): void {
    const track = this.transcriptTrack!;
    switch (op) {
      case 'delete':
        this.operationResult = this.applyDelete(element);
        break;
      case 'trimStart':
        this.operationResult = this.applyTrimStart(element);
        break;
      case 'trimEnd':
        this.operationResult = this.applyTrimEnd(element);
        break;
      case 'excise':
        this.operationResult = this.applyExcise(element, track);
        break;
      case 'removePunctuation':
        this.transcriptTrackManager.removePunctuation(
          element,
          this.fromIndex,
          this.toIndex,
        );
        break;
    }
  }

  private applyDelete(element: VideoTranscriptElement): OperationResult {
    // Remove the element (this will create appropriate removed clip segments)
    const segment = element.getClipTranscriptSegment();
    this.transcriptTrackManager.removeElement(element, TimelineMode.RIPPLE);
    return {
      operation: 'delete',
      removedSegment: new ClipTranscriptSegment(
        segment.getWordsWithPunctuation(),
        segment.mediaStart,
        segment.mediaEnd,
      ),
    };
  }

  private applyTrimStart(
    element: VideoTranscriptElement,
  ): OperationResult | null {
    const wordsToRemove = this.getWordsToRemove(element);

    if (wordsToRemove.length === 0) return null;

    // Calculate new trim start - find first word after removal range
    const segment = element.getClipTranscriptSegment();
    const remainingWords = segment
      .getWordsWithPunctuation()
      .filter((_, idx) => idx > this.toIndex)
      .filter((t): t is ClipTranscriptTextToken => t.type === 'text');

    let newTrimStart;
    if (remainingWords.length > 0) {
      // TODO: trim to first remaining word or to last removed word end time?
      newTrimStart = remainingWords[0].media_ts;
    } else {
      newTrimStart = wordsToRemove[wordsToRemove.length - 1].media_end_ts;
    }

    // Trim element (this will create appropriate removed clip segments)
    const removedSegment =
      this.transcriptTrackManager.adjustTranscriptElementMediaStart(
        element,
        newTrimStart,
        TimelineMode.RIPPLE,
        false,
      );
    return { operation: 'trimStart', removedSegment };
  }

  private applyTrimEnd(
    element: VideoTranscriptElement,
  ): OperationResult | null {
    const wordsToRemove = this.getWordsToRemove(element);

    if (wordsToRemove.length === 0) return null;

    // Calculate new end time - find last word before removal range
    const segment = element.getClipTranscriptSegment();
    const remainingWords = segment
      .getWordsWithPunctuation()
      .filter((_, idx) => idx < this.fromIndex)
      .filter((t): t is ClipTranscriptTextToken => t.type === 'text');

    let newEndTime;
    if (remainingWords.length > 0) {
      const lastRemainingWord = remainingWords[remainingWords.length - 1];
      // as ClipTranscriptTextToken;
      newEndTime = element.time + lastRemainingWord.relative_end_ts;
    } else {
      newEndTime = element.time + wordsToRemove[0].relative_ts;
    }

    // Trim element end (this will create appropriate removed clip segments)
    const removedSegment = this.transcriptTrackManager.trimElementEnd(
      element,
      newEndTime,
      TimelineMode.RIPPLE,
      false,
    );
    return { operation: 'trimEnd', removedSegment };
  }

  private applyExcise(
    element: VideoTranscriptElement,
    track: TranscriptTrack,
  ): OperationResult | null {
    const wordsToRemove = this.getWordsToRemove(element);
    const firstWordToRemove = wordsToRemove[0];
    const lastWordToRemove = wordsToRemove[wordsToRemove.length - 1];

    if (!firstWordToRemove || !lastWordToRemove) return null;

    // Excise the middle section (this will create appropriate removed clip segments)
    const result = this.transcriptTrackManager.exciseFromTranscriptElement(
      element,
      firstWordToRemove.media_ts,
      lastWordToRemove.media_end_ts,
      track,
      TimelineMode.RIPPLE,
    );

    if (!result) return null;
    return { operation: 'excise', removedSegment: result.removedSegment };
  }

  private getWordsToRemove(
    element: VideoTranscriptElement,
  ): ClipTranscriptTextToken[] {
    const segment = element.getClipTranscriptSegment();
    if (!segment) return [];

    return segment
      .getWordsWithPunctuation()
      .filter((_, idx) => idx >= this.fromIndex && idx <= this.toIndex)
      .filter(
        (token): token is ClipTranscriptTextToken => token.type === 'text',
      );
  }
}
